import { FormEvent, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';

interface ForgotPasswordFormProps {
  onBack: () => void;
  onSwitchToRegister?: () => void;
}

export function ForgotPasswordForm({ onBack, onSwitchToRegister }: ForgotPasswordFormProps) {
  const { t } = useTranslation();
  const { requestPasswordReset, resetPassword, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [info, setInfo] = useState('');

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      const resetToken = await requestPasswordReset(email);
      setStep('reset');
      if (resetToken) {
        setToken(resetToken);
        setInfo(t('auth.resetTokenInfo', { token: resetToken }));
      } else {
        setInfo(t('auth.resetEmailSent'));
      }
    } catch (err) {
      // Error handled by store
    }
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await resetPassword(token, newPassword);
      setInfo(t('auth.resetSuccess'));
      onBack();
    } catch (err) {
      // Error handled by store
    }
  };

  const showReset = step === 'reset';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-md p-8 bg-surface dark:bg-surface rounded-lg shadow-xl border border-border"
    >
      <h2 className="text-2xl font-bold text-center mb-6 text-text-primary">
        {t('auth.forgotPasswordTitle')}
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded">
          {error}
        </div>
      )}

      {info && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-800 dark:text-green-300 rounded text-sm break-words">
          {info}
        </div>
      )}

      {!showReset && (
        <form onSubmit={handleRequest} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
              placeholder="max@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-white font-medium rounded-lg transition-colors"
          >
            {isLoading ? t('auth.sendingReset') : t('auth.sendReset')}
          </button>
        </form>
      )}

      {showReset && (
        <form onSubmit={handleReset} className="space-y-4">
          <div>
            <label htmlFor="token" className="block text-sm font-medium text-text-primary mb-1">
              {t('auth.resetTokenLabel')}
            </label>
            <input
              id="token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
              placeholder="Paste reset token"
            />
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-text-primary mb-1">
              {t('auth.newPassword')}
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface-elevated text-text-primary"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-white font-medium rounded-lg transition-colors"
          >
            {isLoading ? t('auth.resetting') : t('auth.resetPassword')}
          </button>
        </form>
      )}

      <div className="mt-6 text-center space-x-4">
        <button onClick={onBack} className="text-primary hover:underline text-sm">
          {t('auth.backToLogin')}
        </button>
        {onSwitchToRegister && (
          <button onClick={onSwitchToRegister} className="text-primary hover:underline text-sm">
            {t('auth.register')}
          </button>
        )}
      </div>
    </motion.div>
  );
}
